import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StreamVideo } from "@/components/StreamVideo";
import { ArrowRight, Camera, Globe2, Users } from "lucide-react";

export function QuickMatchCard({ user, online, isPremium }) {
  const nav = useNavigate();
  const [stream, setStream] = useState(null);
  const [denied, setDenied] = useState(false);
  const streamRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("orbit_media_ok") !== "1") return;
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ video: { width: { ideal: 640 } }, audio: false })
      .then((s) => { if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; } streamRef.current = s; setStream(s); })
      .catch(() => setDenied(true));
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const pref = user.gender_preference && user.gender_preference !== "any" ? { male: "Men", female: "Women", nonbinary: "Non-binary" }[user.gender_preference] : "Everyone";
  const region = user.country_preference && user.country_preference !== "worldwide" ? user.country_preference : "Worldwide";

  return (
    <section className="relative overflow-hidden rounded-[28px] orbit-glass p-2.5 orbit-glow-cyan" data-testid="dashboard-quick-match">
      <div className="grid lg:grid-cols-12 gap-3">
        <div className="relative lg:col-span-5 aspect-[4/3] lg:aspect-auto lg:min-h-[300px] rounded-2xl overflow-hidden bg-slate-900">
          {stream ? (
            <StreamVideo stream={stream} muted mirror className="w-full h-full object-cover" testId="dashboard-camera-preview" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-500 text-xs font-mono" data-testid="dashboard-camera-placeholder">
              <Camera className="w-8 h-8" />{denied ? "CAMERA BLOCKED" : "CAMERA PREVIEW"}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-3 left-3 orbit-chip active !bg-emerald-500/10 !border-emerald-500/40 !text-emerald-300">● READY</div>
          <div className="absolute bottom-3 left-3 text-xs font-mono text-white/90 bg-black/40 rounded px-2 py-1">{user.flag} {user.display_name}</div>
        </div>
        <div className="lg:col-span-7 p-5 lg:p-8 flex flex-col justify-center">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Quick match</div>
          <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">Someone new is one tap away.</h2>
          <p className="text-slate-400 mt-2 max-w-md">Your camera is warmed up. Hit start and we'll pair you with a live person matching your vibe.</p>
          <div className="mt-5 flex flex-wrap gap-2 text-sm">
            <span className="orbit-chip" data-testid="quick-match-region"><Globe2 className="w-3.5 h-3.5" /> {region}</span>
            <span className="orbit-chip" data-testid="quick-match-gender"><Users className="w-3.5 h-3.5" /> {pref}</span>
            {(user.interests || []).slice(0, 3).map((i) => <span key={i} className="orbit-chip">{i}</span>)}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button onClick={() => nav("/match")} className="orbit-btn-primary inline-flex items-center gap-2 text-base" data-testid="dashboard-start-chat-btn">
              Start matching <ArrowRight className="w-4 h-4" />
            </button>
            <div className="inline-flex items-center gap-2 text-xs font-mono text-emerald-300" data-testid="dashboard-online-counter">
              <span className="orbit-live-dot inline-block" /> {online === null ? "…" : online} online now
            </div>
            {!isPremium && <button onClick={() => nav("/pricing")} className="text-xs text-fuchsia-300 hover:text-fuchsia-200" data-testid="quick-match-upgrade-link">Unlock gender & country filters →</button>}
          </div>
        </div>
      </div>
    </section>
  );
}
